import { Node, Edge } from '@xyflow/react'
///////////////////////////////
// Ubiverse Connection Types //
///////////////////////////////
// Ubiverse Node Types
export const UbiverseNodeType__Parent = '__nodeType.parent' as const;
export const UbiverseNodeType__Child = '__nodeType.child' as const;
export type UbiverseNodeType = typeof UbiverseNodeType__Parent | typeof UbiverseNodeType__Child;

export type UbiverseCustomNodeType = 'timelineViewerNode' | 'simulatorNode' | 'buttonNode';

// What a Child client on the channel actually is: a physical ESP32 (or an
// emulation of one) vs a browser simulator session. See docs/device-auth.md
// "Dashboard / SimulatorNode".
export type UbiverseClientKind = 'device' | 'simulator';

export type UbiverseClient = {
  id: string;
  type: UbiverseNodeType;
  kind?: UbiverseClientKind;
  hardware_id?: string;
  auth?: {
    userId?: string;
    isAuthenticated: boolean;
  };
  data?: {
    label?: string;
    intensity?: number;
  }
}

export type UbiverseTimelineData = {
  id: string;
  label: string;
  isMuted: boolean;
  isSolo: boolean;
  data: number[];
}

///////////////////////////////
// Ubiverse Node/Edge Types //
//////////////////////////////
export type UbiverseNode = Node & {
  type: UbiverseCustomNodeType;
};
export type UbiverseEdge = Edge & {};

///////////////////////////////
// Ubiverse Sequence Types //
export type UbiverseSequenceNote = {
  id: string;
  position: number; // In numerators, for instance 1.1 = 0 with 4/4, 1.2 = 1 with 4/4, 2.25 = 5.5 with 4/4
  intensity: number;
}

export type UbiverseSequence = {
  id: string;
  name: string;
  isMuted: boolean;
  notes: UbiverseSequenceNote[];
}
//////////////////////////////
///////////////////////////////
// Ubiverse Custom Node Types //
//////////////////////////////
export type UbiverseTimelineViewerNodeData = {
  
}

///////////////////////////////
// Ubiverse Event Types //
///////////////////////////////

// Ubiverse Event Topics
export const UbiverseEventTopicList = {
  SetData: 'event.set.data',
  SetPlay: 'event.set.play',
  SetMute: 'event.set.mute',
  ClearData: 'event.clear.data',
  AckDataChanged: 'event.ack.dataChanged',
  AckPlayChanged: 'event.ack.playChanged',
  AckMuteChanged: 'event.ack.muteChanged',
  AckBufferUpdated: 'event.ack.bufferUpdated',
  AckPlayDone: 'event.ack.playDone',
  SessionListChanged: 'event.sessionList.changed',
  DeviceRevoked: 'event.device.revoked',
}
export type UbiverseEventTopic = typeof UbiverseEventTopicList[keyof typeof UbiverseEventTopicList]

// Addressing rules (mirrored by firmware ubResolver.ino):
//  - Set* events: a payload with a targetId only reaches the endpoint whose
//    clientId matches (browser clients filter in RealtimeClient; the firmware
//    compares against its hardware_id). No targetId → broadcast, all act.
//  - Ack* events: clientId identifies the sender by the id it is addressable
//    under — hardware_id for firmware, the realtime connection id for
//    simulators. Absent on acks from pre-per-device-play firmware.
export type UbiverseEvent =
  | { topic: typeof UbiverseEventTopicList.SetData; payload: { targetId?: string; data: number[] } }
  | { topic: typeof UbiverseEventTopicList.SetPlay; payload: { isPlaying: boolean; targetId?: string } }
  | { topic: typeof UbiverseEventTopicList.SetMute; payload: { isMute: boolean; nodeId?: string } }
  | { topic: typeof UbiverseEventTopicList.ClearData; payload: null }
  | { topic: typeof UbiverseEventTopicList.AckDataChanged; payload: { clientId?: string } | null }
  | { topic: typeof UbiverseEventTopicList.AckPlayChanged; payload: { isPlaying: boolean; clientId?: string } }
  | { topic: typeof UbiverseEventTopicList.AckMuteChanged; payload: { isMute: boolean; nodeId?: string; clientId?: string } }
  | { topic: typeof UbiverseEventTopicList.AckBufferUpdated; payload: null }
  | { topic: typeof UbiverseEventTopicList.AckPlayDone; payload: null }
  | { topic: typeof UbiverseEventTopicList.SessionListChanged; payload: { sessionList: UbiverseClient[] } }
  | { topic: typeof UbiverseEventTopicList.DeviceRevoked; payload: { hardware_id: string } }

// export const UbiverseEventTopic__Play = 'event.play' as const;
// export const UbiverseEventTopic__Pause = 'event.pause' as const;
// export const UbiverseEventTopic__SetData = 'event.set.data' as const;
// export const UbiverseEventTopic__SetTime = 'event.set.time' as const;
// export const UbiverseEventTopic__SetMute = 'event.set.mute' as const;
// export const UbiverseEventTopic__ClearData = 'event.clear.data' as const;
// export const UbiverseEventTopic__ClearTime = 'event.clear.time' as const;
// export const UbiverseEventTopic__ClearMute = 'event.clear.mute' as const;
// export const UbiverseEventTopic__ClearAll = 'event.clear.all' as const;
// export const UbiverseEventTopic__Trigger = 'event.trigger' as const;
// export const UbiverseEventTopic__Increment = 'event.increment' as const;
// export const UbiverseEventTopic__SimStateChanged = 'event.sim.changed' as const;
// export const UbiverseEventTopic__SessionListChanged = 'event.sessionList.changed' as const;
// export const UbiverseEventTopicList = [
//   UbiverseEventTopic__Play,
//   UbiverseEventTopic__Pause,
//   UbiverseEventTopic__SetData,
//   UbiverseEventTopic__SetTime,
//   UbiverseEventTopic__SetMute,
//   UbiverseEventTopic__ClearData,
//   UbiverseEventTopic__ClearTime,
//   UbiverseEventTopic__ClearMute,
//   UbiverseEventTopic__ClearAll,
//   UbiverseEventTopic__Trigger,
//   UbiverseEventTopic__Increment,
//   UbiverseEventTopic__SimStateChanged,
//   UbiverseEventTopic__SessionListChanged
// ] as const;
// export type UbiverseEventTopic = typeof UbiverseEventTopicList[number];

// // Ubiverse Event Topics
// export type UbiverseEvent = {
//   topic: UbiverseEventTopic;
//   label: string;
//   payloadType?: any; // Update
// }

///////////////////////////////

///////////////////////////////
// Ubiverse Note Data Types //
///////////////////////////////
export type UbiverseSequenceData = {
  intensity: number;
  timestamp: number;
  position: number;
}

export type UbiverseNote = {
  nodeId: string;
  intensity: number;
  timestamp: number;
  position: number;
}
